/**
 * candle — localized, anchored flame lights (candles, torches, braziers).
 *
 * Distinct from `flicker` in two ways:
 *   1. Visual: candle renders a vertically-stretched flame core + warm halo,
 *      not just a round flickering glow.
 *   2. Motion: the flame breathes (scale), shimmers (alpha), and jitters (tip
 *      wobble) with a wind/in-breath feel.
 *
 * Each anchor becomes one candle. Normalized anchors use the initial camera
 * scatter region; absolute world anchors stay attached to authored landmarks.
 * Under reduced motion there is no tick: each flame holds its resting pose.
 */
import { registerEffect, makeContentHandle, type EffectBuilder, type EffectHandle } from '../effectBuilders';
import { placeFlickers, seededRand } from '../../vfx/flickerConfig';
import { createCandleFlame, type CandleFlameParts } from '../../vfx/candleFlameFactory';
import { clamp01 } from '../../../../shared/utils/MathUtils';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';

const buildCandle: EffectBuilder = (layer, ctx): EffectHandle | null => {
  if (layer.kind !== 'candle') return null;

  const spec = layer;
  const placed = placeFlickers(spec.anchors, ctx.region, spec.hzMin ?? 1.2, spec.hzMax ?? 2.8, ctx.seed);
  const candles: CandleFlameParts[] = [];
  const params: {
    x: number;
    y: number;
    hz: number;
    seed: number;
    width: number;
    height: number;
  }[] = [];

  for (const p of placed) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const candle = createCandleFlame({
      x: p.x,
      y: p.y,
      color: spec.color,
      haloColor: spec.haloColor,
      height: spec.height,
      width: spec.width,
      alpha: spec.baseAlpha,
      haloScale: spec.haloScale,
      depth: spec.depth,
      parent: ctx.layerContainer,
    });
    if (!candle) continue;
    candles.push(candle);
    params.push({
      x: p.x,
      y: p.y,
      hz: p.hz,
      seed: p.seed,
      width: spec.width ?? 14,
      height: spec.height ?? 40,
    });
  }

  /** Poses candle `i` for a shimmer in -1..1 and a tip wobble in px. */
  const pose = (i: number, shimmer: number, wobble: number) => {
    const { x, y, width, height } = params[i];
    const c = candles[i];
    // Core alpha: sharp flicker on top of the base.
    c.core.setAlpha(clamp01((spec.baseAlpha ?? 0.9) + shimmer * (spec.flickerAmp ?? 0.25)));
    // Flame breath: vary display height while preserving authored width.
    c.core.setDisplaySize(width, height * (1 + shimmer * 0.12));
    c.core.setPosition(x + wobble, y);
    if (c.halo) {
      c.halo.setAlpha(clamp01(0.35 + shimmer * 0.15));
      c.halo.setPosition(x + wobble * 0.5, y - (spec.height ?? 40) * 0.15);
    }
  };

  const destroyAll = () => candles.forEach((c) => c.destroy());
  const spriteCount = () => candles.reduce((n, c) => n + (c.halo ? 2 : 1), 0);

  // Reduced motion: every flame at rest, and no tick.
  if (getMotionPrefs().reduceMotion) {
    candles.forEach((_, i) => pose(i, 0, 0));
    return makeContentHandle({ spriteCount, onDestroy: destroyAll });
  }

  let elapsedMs = 0;
  const unsubscribe = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    const t = elapsedMs / 1000;
    for (let i = 0; i < candles.length; i += 1) {
      const { hz, seed } = params[i];
      // Two incommensurate sines for organic flicker (same curve as flickerConfig,
      // but scaled for a sharper flame feel).
      const phase1 = seededRand(seed * 1.61 + 7) * Math.PI * 2;
      const phase2 = seededRand(seed * 2.37 + 19) * Math.PI * 2;
      const w = 2 * Math.PI * hz;
      const shimmer = (Math.sin(w * t + phase1) + Math.sin(w * 2.3 * t + phase2)) / 2;
      // Tip wobble: tiny horizontal drift at the flame tip.
      pose(i, shimmer, Math.sin(w * 0.7 * t + phase1) * (spec.wobblePx ?? 1.5));
    }
  });

  return makeContentHandle({
    spriteCount,
    onDestroy: () => {
      unsubscribe();
      destroyAll();
    },
  });
};

registerEffect({ kind: 'candle', build: buildCandle, depth: Z_LAYERS.PROP_LIGHTS });
